import React from "react";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import { CalendarDays, ChevronDown, ChevronUp, Clock3 } from "lucide-react";
import { HoverCard } from "./ResultsPrimitives.jsx";
import usePopoverDismiss from "../hooks/usePopoverDismiss.js";
import {
  getLoadingClientSuggestions,
  maskDisplayDateInputValue,
  normalizeOptionalString,
  parseDisplayDateInputValue,
  resolveEndOfDayLoadHoverCardDetails,
  toDateInputValue,
  toDisplayDateInputValue,
} from "../hooks/useDataManagerOrdersUtils.js";
import { EndOfDayLoadHoverCardDetails } from "./EndOfDayLoadHoverCardDetails.jsx";

export { handleEditableGridKeyDown } from "../utils/dataManagerGridRowStyles.js";

const END_OF_DAY_LOAD_DELIVERY_DATE_LOCK_MESSAGE = "Étant donné que cette commande est en Chargé PM, la date de livraison doit être modifiée via le clic sur Chargé PM.";

export function preventAddressInputDragEvent(event) {
  event.preventDefault();
}

export function Field({ children, className = "", error = "", hint = "", label, labelClassName = "", required = false }) {
  const message = error || hint;
  const resolvedLabelClassName = labelClassName || "text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-500";
  return (
    <label className={["grid min-w-0 gap-0.5", className].join(" ").trim()}>
      <span className={resolvedLabelClassName}>
        {label}
        {required ? <span className="ml-0.5 text-[9px] text-rose-500">*</span> : null}
      </span>
      {children}
      {message ? (
        <span className={`text-[9px] leading-4 ${error ? "text-rose-600" : "text-slate-500"}`}>{message}</span>
      ) : null}
    </label>
  );
}

function applyInputSelection(inputNode, start, end) {
  if (!inputNode || typeof inputNode.setSelectionRange !== "function") {
    return;
  }

  try {
    inputNode.setSelectionRange(start, end);
  } catch {
    // Some browsers block programmatic selection on certain input types.
  }
}

export function selectDateInputDaySegment(inputNode) {
  if (!inputNode) {
    return;
  }

  const value = typeof inputNode.value === "string" ? inputNode.value.trim() : "";
  if (inputNode.type === "text" && inputNode.dataset?.dateSegmentInput === "day-first") {
    if (!/^\d{2}-\d{2}-\d{4}/.test(value)) {
      return;
    }

    const applySelection = () => applyInputSelection(inputNode, 0, 2);
    applySelection();
    requestAnimationFrame(applySelection);
    return;
  }

  if (inputNode.type !== "date" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return;
  }

  const applySelection = () => applyInputSelection(inputNode, 8, 10);
  applySelection();
  requestAnimationFrame(applySelection);
}

export function applyDateInputMask(inputNode) {
  const rawValue = inputNode.value;
  const maskedValue = maskDisplayDateInputValue(rawValue);
  const caret = inputNode.selectionStart ?? rawValue.length;
  const digitCount = rawValue.slice(0, caret).replace(/\D/g, "").length;
  const nextCaret = rawValue === maskedValue ? caret : caret === rawValue.length
    ? maskedValue.length
    : Math.min(maskedValue.length, digitCount + (digitCount > 2 ? 1 : 0) + (digitCount > 4 ? 1 : 0));
  inputNode.value = maskedValue;
  applyInputSelection(inputNode, nextCaret, nextCaret);
  return maskedValue;
}

function resolveDateDisplayParts(displayValue = "") {
  const match = typeof displayValue === "string"
    ? displayValue.trim().match(/^(\d{2})-(\d{2})-(\d{4})(?:\s.+)?$/)
    : null;

  if (!match) {
    return null;
  }

  const [, day, month, year] = match;
  return { day, month, year };
}

function resolveIsoDateValue(value = "", fallbackDisplay = "") {
  const normalizedValue = toDateInputValue(value);
  if (normalizedValue) {
    return normalizedValue;
  }

  const parsedDisplayValue = parseDisplayDateInputValue(fallbackDisplay);
  if (/^\d{4}-\d{2}-\d{2}$/.test(parsedDisplayValue)) {
    return parsedDisplayValue;
  }

  return "";
}

const MONTH_YEAR_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  month: "long",
  year: "numeric",
});

const WEEKDAY_LABELS = ["lu", "ma", "me", "je", "ve", "sa", "di"];

function padCalendarNumber(value) {
  return String(value).padStart(2, "0");
}

function parseIsoDateParts(isoValue = "") {
  const match = typeof isoValue === "string" ? isoValue.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/) : null;
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsedDate = new Date(year, monthIndex, day, 12, 0, 0, 0);

  if (
    Number.isNaN(parsedDate.getTime())
    || parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== monthIndex
    || parsedDate.getDate() !== day
  ) {
    return null;
  }

  return { day, monthIndex, year };
}

function buildIsoDateValue(year, monthIndex, day) {
  return `${year}-${padCalendarNumber(monthIndex + 1)}-${padCalendarNumber(day)}`;
}

function getLocalTodayIsoDate() {
  const currentDate = new Date();
  return buildIsoDateValue(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
}

function buildCalendarMonthCells(year, monthIndex) {
  const firstDayOfMonth = new Date(year, monthIndex, 1, 12, 0, 0, 0);
  const daysInMonth = new Date(year, monthIndex + 1, 0, 12, 0, 0, 0).getDate();
  const leadingEmptyCellCount = (firstDayOfMonth.getDay() + 6) % 7;
  const cells = [];

  for (let emptyIndex = 0; emptyIndex < leadingEmptyCellCount; emptyIndex += 1) {
    cells.push({ key: `empty-start-${emptyIndex}`, type: "empty" });
  }

  for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber += 1) {
    cells.push({
      dayNumber,
      isoValue: buildIsoDateValue(year, monthIndex, dayNumber),
      key: `day-${dayNumber}`,
      type: "day",
    });
  }

  const trailingEmptyCellCount = (7 - (cells.length % 7)) % 7;
  for (let emptyIndex = 0; emptyIndex < trailingEmptyCellCount; emptyIndex += 1) {
    cells.push({ key: `empty-end-${emptyIndex}`, type: "empty" });
  }

  return cells;
}

function DayFirstDatePickerCalendar({
  anchorRef,
  isOpen,
  onClear,
  onClose,
  onSelectDate,
  value = "",
}) {
  const floatingRef = React.useRef(null);
  const selectedIsoValue = resolveIsoDateValue(value);
  const selectedDateParts = parseIsoDateParts(selectedIsoValue);
  const initialViewDate = selectedDateParts || parseIsoDateParts(getLocalTodayIsoDate());
  const [viewYear, setViewYear] = React.useState(() => initialViewDate?.year || new Date().getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = React.useState(() => initialViewDate?.monthIndex || new Date().getMonth());

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextViewDate = parseIsoDateParts(selectedIsoValue) || parseIsoDateParts(getLocalTodayIsoDate());
    if (!nextViewDate) {
      return;
    }

    setViewYear(nextViewDate.year);
    setViewMonthIndex(nextViewDate.monthIndex);
  }, [isOpen, selectedIsoValue]);

  usePopoverDismiss({
    anchorElement: anchorRef?.current,
    floatingRef,
    isOpen,
    onClose,
  });

  if (!isOpen) {
    return null;
  }

  const monthLabel = MONTH_YEAR_LABEL_FORMATTER.format(new Date(viewYear, viewMonthIndex, 1, 12, 0, 0, 0));
  const calendarCells = buildCalendarMonthCells(viewYear, viewMonthIndex);
  const todayIsoValue = getLocalTodayIsoDate();

  const handlePreviousMonth = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (viewMonthIndex === 0) {
      setViewYear((currentYear) => currentYear - 1);
      setViewMonthIndex(11);
      return;
    }

    setViewMonthIndex((currentMonthIndex) => currentMonthIndex - 1);
  };

  const handleNextMonth = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (viewMonthIndex === 11) {
      setViewYear((currentYear) => currentYear + 1);
      setViewMonthIndex(0);
      return;
    }

    setViewMonthIndex((currentMonthIndex) => currentMonthIndex + 1);
  };

  return (
    <div
      ref={floatingRef}
      role="dialog" aria-modal="false" aria-label="Choisir une date"
      className="absolute left-0 top-full z-50 mt-1 w-[17.5rem] rounded-[4px] border border-slate-200 bg-white p-2 shadow-[0_16px_40px_rgba(15,23,42,0.18)]"
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-[12px] font-medium capitalize text-slate-800">{monthLabel}</p>
        <div className="flex items-center gap-0.5">
          <button
            aria-label="Mois précédent"
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            onClick={handlePreviousMonth}
            type="button"
          >
            <ChevronUp size={14} />
          </button>
          <button
            aria-label="Mois suivant"
            className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            onClick={handleNextMonth}
            type="button"
          >
            <ChevronDown size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium uppercase tracking-[0.04em] text-slate-400">
        {WEEKDAY_LABELS.map((weekdayLabel) => (
          <span key={weekdayLabel}>{weekdayLabel}</span>
        ))}
      </div>

      <div className="mt-1 grid grid-cols-7 gap-0.5">
        {calendarCells.map((cell) => {
          if (cell.type === "empty") {
            return <span key={cell.key} aria-hidden="true" />;
          }

          const isSelected = selectedDateParts
            && cell.isoValue === buildIsoDateValue(selectedDateParts.year, selectedDateParts.monthIndex, selectedDateParts.day);
          const isToday = cell.isoValue === todayIsoValue;

          return (
            <button
              key={cell.key}
              className={[
                "inline-flex h-6 w-full items-center justify-center rounded-[4px] text-[12px] transition",
                isSelected
                  ? "bg-accent font-semibold text-white"
                  : isToday
                    ? "border border-slate-300 font-medium text-slate-900"
                    : "text-slate-700 hover:bg-slate-100",
              ].join(" ")}
              onClick={() => onSelectDate?.(cell.isoValue)}
              type="button"
            >
              {cell.dayNumber}
            </button>
          );
        })}
      </div>

      <div className="mt-1.5 flex items-center justify-between border-t border-slate-200 pt-1 text-[12px]">
        <button
          className="font-medium text-accent transition hover:text-accent-strong"
          onClick={() => onClear?.()}
          type="button"
        >
          Effacer
        </button>
        <button
          className="font-medium text-accent transition hover:text-accent-strong"
          onClick={() => onSelectDate?.(todayIsoValue)}
          type="button"
        >
          Aujourd&apos;hui
        </button>
      </div>
    </div>
  );
}

function DayFirstDatePickerInput({
  buttonClassName,
  inputClassName,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  value = "",
  ...inputProps
}) {
  const containerRef = React.useRef(null);
  const textInputRef = React.useRef(null);
  const skipBlurCommitRef = React.useRef(false);
  const restoreFocusAfterCalendarRef = React.useRef(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
  const [editText, setEditText] = React.useState(() => toDisplayDateInputValue(value));

  React.useEffect(() => {
    if (!isFocused) {
      setEditText(toDisplayDateInputValue(value));
    }
  }, [isFocused, value]);

  const displayText = isFocused ? editText : toDisplayDateInputValue(value);

  const emitIsoValue = React.useCallback((nextIsoValue) => {
    onChange?.({
      target: {
        value: nextIsoValue,
      },
    });
  }, [onChange]);

  const applyIsoValue = React.useCallback((nextIsoValue, { keepFocus = false } = {}) => {
    const nextDisplayText = nextIsoValue ? toDisplayDateInputValue(nextIsoValue) : "";
    setEditText(nextDisplayText);
    emitIsoValue(nextIsoValue);

    if (keepFocus) {
      restoreFocusAfterCalendarRef.current = true;
      setIsFocused(true);
      requestAnimationFrame(() => {
        const inputNode = textInputRef.current;
        if (!inputNode) {
          return;
        }

        inputNode.focus({ preventScroll: true });
        selectDateInputDaySegment(inputNode);
      });
      return;
    }

    skipBlurCommitRef.current = true;
    setIsFocused(false);
    textInputRef.current?.blur();
  }, [emitIsoValue]);

  const handleOpenPicker = React.useCallback(() => {
    setIsCalendarOpen((currentValue) => !currentValue);
  }, []);

  const handleCloseCalendar = React.useCallback(() => {
    setIsCalendarOpen(false);
  }, []);

  const handleSelectCalendarDate = React.useCallback((nextIsoValue) => {
    applyIsoValue(nextIsoValue, { keepFocus: true });
    setIsCalendarOpen(false);
  }, [applyIsoValue]);

  const handleClearCalendarDate = React.useCallback(() => {
    applyIsoValue("", { keepFocus: true });
    setIsCalendarOpen(false);
  }, [applyIsoValue]);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <input
        {...inputProps}
        ref={textInputRef}
        className={inputClassName}
        data-date-segment-input="day-first"
        inputMode="numeric"
        onBlur={(event) => {
          setIsFocused(false);
          if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            onBlur?.(event);
            return;
          }
          const nextIsoValue = parseDisplayDateInputValue(editText);
          if (nextIsoValue) {
            emitIsoValue(nextIsoValue);
            setEditText(toDisplayDateInputValue(nextIsoValue));
          } else if (!editText.trim()) {
            emitIsoValue("");
            setEditText("");
          } else {
            setEditText(toDisplayDateInputValue(value));
          }
          onBlur?.(event);
        }}
        onChange={(event) => {
          const nextEditText = applyDateInputMask(event.target);
          setEditText(nextEditText);
          const nextIsoValue = parseDisplayDateInputValue(nextEditText);
          if (/^\d{2}-\d{2}-\d{4}$/.test(nextEditText) && nextIsoValue) {
            emitIsoValue(nextIsoValue);
          } else if (!nextEditText.trim()) {
            emitIsoValue("");
          }
        }}
        onFocus={(event) => {
          if (restoreFocusAfterCalendarRef.current) {
            restoreFocusAfterCalendarRef.current = false;
            setIsFocused(true);
            selectDateInputDaySegment(event.target);
            onFocus?.(event);
            return;
          }

          const nextEditText = toDisplayDateInputValue(value);
          setEditText(nextEditText);
          setIsFocused(true);
          setIsCalendarOpen(false);
          selectDateInputDaySegment(event.target);
          onFocus?.(event);
        }}
        onKeyDown={onKeyDown}
        type="text"
        value={displayText}
      />
      <button
        aria-expanded={isCalendarOpen}
        aria-label="Ouvrir le calendrier"
        className={[buttonClassName, "z-40"].filter(Boolean).join(" ")}
        disabled={inputProps.disabled}
        onClick={handleOpenPicker}
        onMouseDown={(event) => event.preventDefault()}
        tabIndex={-1}
        type="button"
      >
        <CalendarDays size={12} />
      </button>
      <DayFirstDatePickerCalendar
        anchorRef={containerRef}
        isOpen={isCalendarOpen && !inputProps.disabled}
        onClear={handleClearCalendarDate}
        onClose={handleCloseCalendar}
        onSelectDate={handleSelectCalendarDate}
        value={value}
      />
    </div>
  );
}

export function PickerInput({
  buttonClassName,
  displayValue = "",
  displayValueClassName = "",
  inputClassName,
  selectDayOnFocus = false,
  type = "date",
  ...inputProps
}) {
  if (selectDayOnFocus && type === "date") {
    return (
      <DayFirstDatePickerInput
        buttonClassName={buttonClassName}
        inputClassName={inputClassName}
        {...inputProps}
      />
    );
  }

  const inputRef = React.useRef(null);
  const [isFocused, setIsFocused] = React.useState(false);
  const PickerIcon = type === "time" ? Clock3 : CalendarDays;
  const resolvedDisplayValue = typeof displayValue === "string" ? displayValue.trim() : "";
  const dateDisplayParts = type === "date" ? resolveDateDisplayParts(resolvedDisplayValue) : null;
  const shouldHighlightDay = Boolean(selectDayOnFocus && isFocused && dateDisplayParts);
  const shouldShowFormattedOverlay = Boolean(resolvedDisplayValue) && (type === "date" || !isFocused);
  const resolvedInputClassName = [
    inputClassName,
    shouldShowFormattedOverlay ? "text-transparent caret-transparent" : "",
  ].filter(Boolean).join(" ");
  const resolvedInputStyle = shouldShowFormattedOverlay
    ? {
        ...(inputProps.style || {}),
        WebkitTextFillColor: "transparent",
        caretColor: "transparent",
        color: "transparent",
      }
    : inputProps.style;

  const handleOpenPicker = React.useCallback(() => {
    const inputNode = inputRef.current;
    if (!inputNode) {
      return;
    }

    inputNode.focus({ preventScroll: true });
    if (typeof inputNode.showPicker === "function") {
      try {
        inputNode.showPicker();
        return;
      } catch {
        // Ignore browsers that block showPicker and fall back to click.
      }
    }

    inputNode.click();
  }, []);

  return (
    <div className="relative min-w-0">
      <input
        {...inputProps}
        ref={inputRef}
        className={resolvedInputClassName}
        onBlur={(event) => {
          setIsFocused(false);
          inputProps.onBlur?.(event);
        }}
        onFocus={(event) => {
          setIsFocused(true);
          if (selectDayOnFocus && type === "date") {
            selectDateInputDaySegment(event.target);
          }
          inputProps.onFocus?.(event);
        }}
        style={resolvedInputStyle}
        type={type}
      />
      {shouldShowFormattedOverlay ? (
        <span className={[
          "pointer-events-none absolute inset-y-px left-px right-8 z-30 flex items-center truncate text-slate-700",
          displayValueClassName,
        ].filter(Boolean).join(" ")}>
          {shouldHighlightDay ? (
            <>
              <span className="rounded bg-accent/15 px-0.5 font-medium text-slate-900">{dateDisplayParts.day}</span>
              <span>-{dateDisplayParts.month}-{dateDisplayParts.year}</span>
            </>
          ) : resolvedDisplayValue}
        </span>
      ) : null}
      <button
        aria-label={type === "time" ? "Ouvrir le sélecteur d'heure" : "Ouvrir le calendrier"}
        className={[buttonClassName, "z-40"].filter(Boolean).join(" ")}
        disabled={inputProps.disabled}
        onClick={handleOpenPicker}
        onMouseDown={(event) => event.preventDefault()}
        tabIndex={-1}
        type="button"
      >
        <PickerIcon size={12} />
      </button>
    </div>
  );
}

export function LoadingClientAutocompleteInput({
  className,
  getSuggestions = getLoadingClientSuggestions,
  required = false,
  suggestionsClassName = "rounded-[10px]",
  dataGridFocusKey = undefined,
  disabled = false,
  error = "",
  inputRef = null,
  listId,
  onBlur = null,
  onFocus = null,
  onKeyDown = null,
  onValueChange,
  options = [],
  placeholder,
  value,
}) {
  const containerRef = React.useRef(null);
  const localInputRef = React.useRef(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const suggestions = getSuggestions(value, options);
  const shouldShowSuggestions = isSuggestionsOpen && suggestions.length > 0;
  const suggestionsListId = listId ? `${listId}-suggestions` : undefined;
  const { floatingStyles, isPositioned, refs } = useFloating({
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
    open: shouldShowSuggestions,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [value, suggestions.length]);

  const assignInputRef = React.useCallback((node) => {
    localInputRef.current = node;
    refs.setReference(node);
    if (typeof inputRef === "function") {
      inputRef(node);
      return;
    }
    if (inputRef) {
      inputRef.current = node;
    }
  }, [inputRef, refs]);

  const handleSelectSuggestion = React.useCallback((nextValue) => {
    onValueChange(nextValue);
    setIsSuggestionsOpen(false);
    localInputRef.current?.focus();
  }, [onValueChange]);

  const handleInputFocus = React.useCallback((event) => {
    setIsSuggestionsOpen(true);
    onFocus?.(event);
  }, [onFocus]);

  const handleInputBlur = React.useCallback((event) => {
    window.requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (containerRef.current?.contains(activeElement)) {
        return;
      }
      if (refs.floating.current?.contains(activeElement)) {
        return;
      }
      setIsSuggestionsOpen(false);
      onBlur?.(event);
    });
  }, [onBlur, refs.floating]);

  const handleInputChange = React.useCallback((event) => {
    setIsSuggestionsOpen(true);
    onValueChange(event.target.value);
  }, [onValueChange]);

  const handleInputKeyDown = React.useCallback((event) => {
    if (shouldShowSuggestions) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((currentIndex) => (
          currentIndex + 1 < suggestions.length ? currentIndex + 1 : 0
        ));
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((currentIndex) => (
          currentIndex - 1 >= 0 ? currentIndex - 1 : suggestions.length - 1
        ));
        return;
      }

      if (event.key === "Enter" && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        event.preventDefault();
        handleSelectSuggestion(suggestions[highlightedIndex].value);
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setIsSuggestionsOpen(false);
        return;
      }
    }

    onKeyDown?.(event);
  }, [handleSelectSuggestion, highlightedIndex, onKeyDown, shouldShowSuggestions, suggestions]);

  const suggestionsList = shouldShowSuggestions ? (
    <ul
      id={suggestionsListId}
      ref={refs.setFloating}
      data-loading-client-suggestions="true"
      className={`z-popover max-h-44 min-w-[12rem] overflow-y-auto border border-slate-200 bg-white py-1 shadow-[0_12px_28px_rgba(15,23,42,0.14)] ${suggestionsClassName}`}
      role="listbox"
      style={{
        ...floatingStyles,
        opacity: isPositioned ? 1 : 0,
        pointerEvents: isPositioned ? undefined : "none",
      }}
    >
      {suggestions.map((option, index) => (
        <li key={option.lookupKey} role="presentation">
          <button
            type="button"
            role="option"
            aria-selected={index === highlightedIndex}
            className={[
              "flex w-full items-center px-2.5 py-1.5 text-left text-[11.5px] text-slate-700 transition-colors duration-150 hover:bg-slate-50",
              index === highlightedIndex ? "bg-slate-100 text-slate-950" : "",
            ].filter(Boolean).join(" ")}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => setHighlightedIndex(index)}
            onClick={() => handleSelectSuggestion(option.value)}
          >
            {option.label ?? option.value}
          </button>
        </li>
      ))}
    </ul>
  ) : null;

  return (
    <div ref={containerRef} className="relative min-w-0 overflow-visible">
      <input
        aria-autocomplete="list"
        aria-invalid={Boolean(error) || undefined}
        aria-controls={shouldShowSuggestions ? suggestionsListId : undefined}
        aria-expanded={shouldShowSuggestions}
        autoComplete="off"
        className={className}
        data-grid-focus-key={dataGridFocusKey}
        disabled={disabled}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        ref={assignInputRef}
        role="combobox"
        required={required}
        title={error || undefined}
        type="text"
        value={value}
      />
      {typeof document !== "undefined" && suggestionsList
        ? createPortal(suggestionsList, getReactPortalRoot())
        : suggestionsList}
    </div>
  );
}

export function LoadingAddressHoverCard({
  address,
  children,
  title = "Adresse de chargement",
}) {
  const resolvedAddress = normalizeOptionalString(address);
  if (!resolvedAddress) {
    return children;
  }

  return (
    <HoverCard
      content={<span className="block whitespace-pre-wrap text-[11px] leading-5 text-slate-700">{resolvedAddress}</span>}
      maxWidth={360}
      placement="top"
      title={title}
    >
      {children}
    </HoverCard>
  );
}

export function EndOfDayLoadHoverCard({
  children,
  order,
}) {
  const details = resolveEndOfDayLoadHoverCardDetails(order);
  if (!details) {
    return children;
  }

  return (
    <HoverCard
      accentColor="var(--color-accent)"
      content={<EndOfDayLoadHoverCardDetails details={details} />}
      maxWidth={360}
      placement="top"
      title="Chargé en PM"
    >
      {children}
    </HoverCard>
  );
}

export function EndOfDayLoadDeliveryDateHoverCard({
  children,
}) {
  return (
    <HoverCard
      accentColor="var(--color-accent)"
      content={(
        <span className="block text-[11px] leading-5 text-slate-700">
          {END_OF_DAY_LOAD_DELIVERY_DATE_LOCK_MESSAGE}
        </span>
      )}
      maxWidth={320}
      placement="top"
      title="Date verrouillée"
    >
      <div className="min-w-0" title="">
        {children}
      </div>
    </HoverCard>
  );
}
